import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme } from '../types';
import {
  ResourcePool, ResourcePoolMember, DEFAULT_HOURS_PER_DAY,
  getTenantPool, createResourcePool,
  listPoolMembers, createPoolMember, updatePoolMember, deletePoolMember,
  countActiveAssignedTasks,
} from '../services/resourcePoolService';
import { graphGet } from '../api/graphClient';
import { getOrCreateTenantConfig, setTenantRFPEnabled, getBDLeads, setBDLeads, BDLead } from '../services/tenantConfigService';
import { listRFPs } from '../services/rfpService';
import { computeWinRate, winRateCsv } from '../services/rfpAnalyticsService';
import WorkCalendarsAdminCard from '../components/settings/WorkCalendarsAdminCard';
import {
  Tag, TagPool, TagScope, TASK_TAG_POOL, DECISION_TAG_POOL,
  getAllTags, getTag, promoteTagToTenant, createTenantTag, findTagByName,
} from '../services/tagService';
import PeoplePicker, { Person } from '../components/PeoplePicker';
import DeleteTagModal from '../components/DeleteTagModal';
import LicenseActivation from '../components/LicenseActivation';
import { readStoredLicenseKey } from '../services/licenseService';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT,
} from '../components/BoardModal';

interface ViewProps { pca: IPublicClientApplication; theme: AppTheme; }

export default function Admin({ pca }: ViewProps) {
  const nav = useNavigate();
  const [rfpEnabled, setRfpEnabled] = useState<boolean | null>(null);
  const [rfpSaving, setRfpSaving] = useState(false);

  // Load tenant config for the RFP Pipeline toggle. Failure leaves the
  // toggle disabled — the rest of the page is independent so we don't
  // surface an error UI for it.
  useEffect(() => {
    let cancelled = false;
    getOrCreateTenantConfig(pca)
      .then(cfg => { if (!cancelled) setRfpEnabled(!!cfg.dagrun_pm_rfpenabled); })
      .catch(() => { if (!cancelled) setRfpEnabled(false); });
    return () => { cancelled = true; };
  }, [pca]);

  const toggleRFP = async () => {
    if (rfpEnabled === null || rfpSaving) return;
    const next = !rfpEnabled;
    setRfpSaving(true);
    setRfpEnabled(next);
    try {
      await setTenantRFPEnabled(pca, next);
    } catch {
      setRfpEnabled(!next);
    } finally {
      setRfpSaving(false);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 1000 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>TENANT ADMINISTRATION</div>
      <h2 style={{ marginBottom: 24 }}>Admin</h2>

      <div style={{ display: 'grid', gap: 24 }}>
        {/* License — enter/update this tenant's Dagrun PM license key */}
        <LicenseCard pca={pca} />

        {/* Global resource pool — one pool per tenant */}
        <ResourcePoolCard pca={pca} />

        {/* Default module configuration — currently just the tenant-
            level RFP Pipeline toggle. When enabled, an "RFP Pipeline"
            entry appears on the Projects toolbar that opens /rfp. */}
        <div className="card" style={{ padding: 20 }}>
          <div className="eyebrow">DEFAULT MODULE CONFIGURATION</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8, marginBottom: 14 }}>
            Tenant-wide module switches. Per-project module flags live on each project's Settings tab.
          </div>
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px',
            border: '0.5px solid var(--border-light)',
            borderRadius: 'var(--r-md)',
            background: rfpEnabled ? 'var(--brand-mist)' : 'var(--surface-2)',
            cursor: rfpEnabled === null || rfpSaving ? 'default' : 'pointer',
            opacity: rfpEnabled === null ? 0.6 : 1,
            transition: 'background 0.12s',
          }}>
            <input
              type="checkbox"
              checked={!!rfpEnabled}
              disabled={rfpEnabled === null || rfpSaving}
              onChange={toggleRFP}
              style={{ marginTop: 2 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>RFP Pipeline</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                Pre-project pipeline for tracking proposals/bids. Independent of any single project.
                Enabling this exposes the RFP Pipeline button on the Projects page.
              </div>
            </div>
          </label>
        </div>

        {/* Work Calendars — tenant work-day/hours calendars + default */}
        <WorkCalendarsAdminCard pca={pca} />

        {/* RFP Pipeline — BD Lead roster + win-rate analytics export */}
        <RFPPipelineCard pca={pca} />

        {/* Tags — separate Task and Decision pools, never merged */}
        <TagsAdminCard pca={pca} />

        {/* Portfolio timeline card — quick link */}
        <div className="card" style={{ padding: 20 }}>
          <div className="eyebrow">PORTFOLIO VIEWS</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8 }}>
            Cross-project timeline with dependencies and slip detection.
          </div>
          <button className="btn-secondary" style={{ marginTop: 14 }} onClick={() => nav('/portfolio/timeline')}>
            Open Portfolio Timeline
          </button>
        </div>
      </div>

    </div>
  );
}

// ── License admin card — enter/update this tenant's license key ─────────
function LicenseCard({ pca }: { pca: IPublicClientApplication }) {
  const [current, setCurrent] = useState<string | null | undefined>(undefined); // undefined = loading
  useEffect(() => {
    let cancelled = false;
    readStoredLicenseKey(pca)
      .then(k => { if (!cancelled) setCurrent(k); })
      .catch(() => { if (!cancelled) setCurrent(null); });
    return () => { cancelled = true; };
  }, [pca]);
  const mask = (k: string) => (k.length > 12 ? `${k.slice(0, 8)}…${k.slice(-4)}` : '••••');
  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="eyebrow">LICENSE</div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8 }}>
        {current === undefined
          ? 'Checking license…'
          : current
            ? <>A tenant license key is active (<code style={{ fontFamily: 'var(--font-mono)' }}>{mask(current)}</code>). Enter a new key below to replace it.</>
            : 'This tenant is using the built-in license. Enter a license key below to activate a tenant-specific license.'}
      </div>
      <LicenseActivation pca={pca} />
    </div>
  );
}

// ── RFP Pipeline admin card — BD Lead roster + win-rate export ──────────
function RFPPipelineCard({ pca }: { pca: IPublicClientApplication }) {
  const [leads, setLeads] = useState<BDLead[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getBDLeads(pca).then(l => { if (!cancelled) setLeads(l); }).catch(() => { if (!cancelled) setLeads([]); });
    return () => { cancelled = true; };
  }, [pca]);

  const persist = async (next: BDLead[]) => {
    setLeads(next); setSaving(true);
    try { await setBDLeads(pca, next); } catch { /* leave optimistic value */ } finally { setSaving(false); }
  };
  const addLead = (p: Person | null) => {
    if (!p || !leads) return;
    if (leads.some(l => l.oid === p.aadObjectId)) return;
    persist([...leads, { oid: p.aadObjectId, name: p.displayName }]);
  };
  const removeLead = (oid: string) => { if (leads) persist(leads.filter(l => l.oid !== oid)); };

  const exportWinRate = async () => {
    setExporting(true); setSummary(null);
    try {
      const rfps = await listRFPs(pca);
      const analytics = computeWinRate(rfps);
      setSummary(analytics.overall.winRatePct == null
        ? 'No decided RFPs yet.'
        : `Overall win rate: ${analytics.overall.winRatePct}% (${analytics.overall.won} won of ${analytics.overall.decided} decided).`);
      const csv = winRateCsv(rfps);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'rfp-win-rate.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setSummary('Could not build the export.');
    } finally { setExporting(false); }
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="eyebrow">RFP PIPELINE</div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8, marginBottom: 16 }}>
        Business-development leads for the pipeline, and win-rate analytics by BD lead, client, source, and quarter.
      </div>

      {/* BD Lead roster */}
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
        BD Leads {saving && <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>· saving…</span>}
      </div>
      {leads === null ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
      ) : (
        <>
          {leads.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-4)', marginBottom: 8 }}>No BD leads assigned yet.</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {leads.map(l => (
              <span key={l.oid} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)', border: '0.5px solid var(--border-light)', borderRadius: 999, padding: '4px 6px 4px 12px', fontSize: 13, color: 'var(--ink)' }}>
                {l.name}
                <button type="button" onClick={() => removeLead(l.oid)} aria-label="Remove"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 15 }}>×</button>
              </span>
            ))}
          </div>
          <div style={{ maxWidth: 320 }}>
            <PeoplePicker pca={pca} value={null} onChange={addLead} placeholder="Add a BD lead…" />
          </div>
        </>
      )}

      {/* Win-rate export */}
      <div style={{ borderTop: '0.5px solid var(--border-light)', marginTop: 18, paddingTop: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>Win-rate analytics</div>
        <button className="btn-secondary" onClick={exportWinRate} disabled={exporting}>
          {exporting ? 'Building…' : 'Export win-rate CSV'}
        </button>
        {summary && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>{summary}</div>}
      </div>
    </div>
  );
}

// ── Tags admin ────────────────────────────────────────────────────────
// Task Tags and Decision Tags are separate pools with separate meaning —
// they render as two sub-tabs and are never merged into one list. Access
// mirrors the rest of the Tenant Admin Panel (route-level exposure via the
// Teams manifest; there is no per-user admin role check anywhere on /admin).
function TagsAdminCard({ pca }: { pca: IPublicClientApplication }) {
  const [pool, setPool] = useState<TagPool>(TASK_TAG_POOL);
  const [tags, setTags] = useState<Tag[] | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [promoting, setPromoting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createDraft, setCreateDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [duplicate, setDuplicate] = useState<Tag | null>(null);
  // Admin can delete any tag, either scope — full authority here.
  const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<Tag | null>(null);

  const reload = useCallback(async (p: TagPool) => {
    setTags(null); setError(null); setDuplicate(null); setCreateDraft('');
    try { setTags(await getAllTags(pca, p)); }
    catch (e) { setTags([]); setError(e instanceof Error ? e.message : 'Failed to load tags'); }
  }, [pca]);
  useEffect(() => { reload(pool); }, [reload, pool]);

  // Direct creation — always Tenant-scoped; Admin's natural authority level.
  // Dedupe is case-insensitive against ALL tags in the pool, any scope: if a
  // match exists we surface it (highlighted row) instead of creating a twin.
  const create = async () => {
    const name = createDraft.trim();
    if (!name || creating || tags === null) return;
    const existing = findTagByName(tags, name);
    if (existing) { setDuplicate(existing); return; }
    setCreating(true); setError(null);
    try {
      const fresh = await createTenantTag(pca, pool, name);
      setTags(prev => [fresh, ...(prev ?? [])]);
      setCreateDraft(''); setDuplicate(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setCreating(false); }
  };

  const sorted = [...(tags ?? [])].sort((a, b) =>
    sortDir === 'desc' ? b.useCount - a.useCount : a.useCount - b.useCount);

  // window.confirm is suppressed inside the Teams webview (returns false
  // silently), which made this button a no-op — confirmation must go
  // through BoardModal like every other destructive/major action.
  const promote = async (tag: Tag) => {
    if (promoting) return;
    setPromoteTarget(null);
    setPromoting(tag.id); setError(null);
    try {
      await promoteTagToTenant(pca, pool, tag.id);
      // Verify by re-fetching the record rather than trusting the PATCH.
      const fresh = await getTag(pca, pool, tag.id);
      if (fresh.scope !== TagScope.Tenant || fresh.projectId !== null) {
        throw new Error('Promotion did not persist — refetch shows unchanged record');
      }
      setTags(prev => (prev ?? []).map(t => t.id === tag.id ? { ...fresh, projectName: null } : t));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Promote failed');
    } finally { setPromoting(null); }
  };

  const subTab = (p: TagPool, label: string) => {
    const active = pool.key === p.key;
    return (
      <button
        key={p.key}
        type="button"
        onClick={() => setPool(p)}
        style={{
          padding: '5px 12px', fontSize: 12, fontWeight: active ? 600 : 500,
          fontFamily: 'var(--font-ui)', borderRadius: 'var(--r-sm)', cursor: 'pointer',
          border: '1px solid ' + (active ? 'var(--brand)' : 'var(--border)'),
          background: active ? 'var(--brand-mist)' : 'var(--surface)',
          color: active ? 'var(--brand)' : 'var(--ink-3)',
        }}
      >{label}</button>
    );
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div className="eyebrow">TAGS</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
            Task tags and decision tags across all projects. Sort by use count to spot
            promotion candidates — promoting a tag to Tenant scope makes it available in every project.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {subTab(TASK_TAG_POOL, 'Task Tags')}
          {subTab(DECISION_TAG_POOL, 'Decision Tags')}
        </div>
      </div>

      {/* Direct tenant-scoped creation */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <input
          value={createDraft}
          disabled={creating || tags === null}
          onChange={e => { setCreateDraft(e.target.value); setDuplicate(null); }}
          onKeyDown={e => { if (e.key === 'Enter') create(); }}
          placeholder={`New tenant-wide ${pool.key} tag…`}
          style={{ flex: '0 1 280px', fontSize: 13 }}
        />
        <button
          type="button"
          className="btn-secondary"
          onClick={create}
          disabled={creating || !createDraft.trim() || tags === null}
          style={{ whiteSpace: 'nowrap' }}
        >{creating ? 'Creating…' : 'Create Tag'}</button>
      </div>
      {duplicate && (
        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--ink-2)' }}>
          A tag named <strong>{duplicate.name}</strong> already exists
          ({duplicate.scope === TagScope.Tenant ? 'Tenant' : `Project: ${duplicate.projectName ?? '—'}`}) — highlighted below.
        </div>
      )}
      {!duplicate && <div style={{ marginBottom: 8 }} />}

      {error && <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

      {tags === null ? (
        <div className="spinner" style={{ margin: '40px auto' }} />
      ) : sorted.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
          No {pool.key} tags yet. Tags are created from the {pool.key === 'task' ? 'task drawer' : 'decision panel'} inside a project.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
            <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Name</th>
            <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Scope</th>
            <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Project</th>
            <th
              onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
              title="Sort by use count"
              style={{ padding: 8, fontSize: 12, color: 'var(--brand)', fontWeight: 500, cursor: 'pointer', userSelect: 'none' }}
            >Use Count {sortDir === 'desc' ? '↓' : '↑'}</th>
            <th style={{ padding: 8 }} />
          </tr></thead>
          <tbody>
            {sorted.map(t => {
              const isTenant = t.scope === TagScope.Tenant;
              return (
                <tr key={t.id} style={{
                  borderBottom: '0.5px solid var(--border-light)',
                  background: duplicate?.id === t.id ? 'var(--brand-mist)' : undefined,
                }}>
                  <td style={{ padding: 10, fontSize: 14, color: 'var(--ink)' }}>{t.name}</td>
                  <td style={{ padding: 10 }}>
                    <span className={`pill ${isTenant ? '' : 'neutral'}`}>{isTenant ? 'Tenant' : 'Project'}</span>
                  </td>
                  <td style={{ padding: 10, fontSize: 12, color: 'var(--ink-3)' }}>
                    {isTenant ? '—' : (t.projectName ?? '—')}
                  </td>
                  <td style={{ padding: 10, fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>{t.useCount}</td>
                  <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!isTenant && (
                      <button
                        type="button"
                        onClick={() => setPromoteTarget(t)}
                        disabled={promoting !== null}
                        style={{
                          background: 'transparent', border: '1px solid var(--brand)', color: 'var(--brand)',
                          padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                          cursor: promoting ? 'default' : 'pointer', whiteSpace: 'nowrap',
                          opacity: promoting && promoting !== t.id ? 0.5 : 1,
                          marginRight: 6,
                        }}
                      >{promoting === t.id ? 'Promoting…' : 'Promote to Tenant'}</button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(t)}
                      disabled={promoting !== null}
                      style={{
                        background: 'transparent', border: '1px solid #DC2626', color: '#DC2626',
                        padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {promoteTarget && (
        <BoardModal open title="Promote to Tenant" onClose={() => setPromoteTarget(null)}
          footer={<>
            <BMCancelButton onClick={() => setPromoteTarget(null)} />
            <BMPrimaryButton onClick={() => promote(promoteTarget)}>Promote</BMPrimaryButton>
          </>}>
          Promote <strong>{promoteTarget.name}</strong> to Tenant scope? It becomes
          available in every project. Existing usage is unaffected.
        </BoardModal>
      )}

      {deleteTarget && (
        <DeleteTagModal
          pca={pca}
          pool={pool}
          tag={deleteTarget}
          tenantWide={deleteTarget.scope === TagScope.Tenant}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setTags(prev => (prev ?? []).filter(t => t.id !== deleteTarget.id));
            setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
}


// ── Global Resource Pool ──────────────────────────────────────────────
// One pool per tenant. Members carry role, default hours/day, and a
// default hourly rate. Access mirrors the rest of the Tenant Admin Panel
// (route-level authority — no per-user role check exists anywhere on
// /admin); the rate column shows here on that authority, while
// project-facing surfaces gate rates by PM role.

const POOL_PROMPT_DISMISSED = 'dagrun_pm_pool_setup_prompt_dismissed';

function ResourcePoolCard({ pca }: { pca: IPublicClientApplication }) {
  // undefined = loading; null = tenant has no pool yet
  const [pool, setPool] = useState<ResourcePool | null | undefined>(undefined);
  const [members, setMembers] = useState<ResourcePoolMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creatingPool, setCreatingPool] = useState(false);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [promptDismissed, setPromptDismissed] = useState(() => {
    try { return window.localStorage.getItem(POOL_PROMPT_DISMISSED) === '1'; } catch { return false; }
  });
  // add-member form
  const [adding, setAdding] = useState(false);
  const [newPerson, setNewPerson] = useState<Person | null>(null);
  const [newRole, setNewRole] = useState('');
  const [newHours, setNewHours] = useState(String(DEFAULT_HOURS_PER_DAY));
  const [newRate, setNewRate] = useState('');
  const [saving, setSaving] = useState(false);
  // remove flow — live junction count drives the warning wording
  const [removeTarget, setRemoveTarget] = useState<ResourcePoolMember | null>(null);
  const [removeTaskCount, setRemoveTaskCount] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);

  const reloadMembers = useCallback(async (poolId: string) => {
    try { setMembers(await listPoolMembers(pca, poolId)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load members'); }
  }, [pca]);

  useEffect(() => {
    let cancelled = false;
    getTenantPool(pca)
      .then(p => {
        if (cancelled) return;
        setPool(p);
        if (p) reloadMembers(p.dagrun_pm_resourcepoolid);
      })
      .catch(e => { if (!cancelled) { setPool(null); setError(e instanceof Error ? e.message : 'Failed to load pool'); } });
    return () => { cancelled = true; };
  }, [pca, reloadMembers]);

  // Tenant user count — only needed to decide whether to show the setup
  // prompt when no pool exists. Best-effort; failure just hides the prompt.
  useEffect(() => {
    if (pool !== null) return;
    let cancelled = false;
    graphGet<{ '@odata.count'?: number }>(pca, '/users?$count=true&$top=1', { ConsistencyLevel: 'eventual' })
      .then(d => { if (!cancelled) setUserCount(d['@odata.count'] ?? null); })
      .catch(() => { if (!cancelled) setUserCount(null); });
    return () => { cancelled = true; };
  }, [pca, pool]);

  const createPool = async () => {
    if (creatingPool) return;
    setCreatingPool(true); setError(null);
    try {
      await createResourcePool(pca, {
        name: 'Global Resource Pool',
        description: 'Tenant-wide resource pool shared by all projects.',
        isActive: true,
      });
      const p = await getTenantPool(pca);
      setPool(p);
      if (p) reloadMembers(p.dagrun_pm_resourcepoolid);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create pool');
    } finally { setCreatingPool(false); }
  };

  const dismissPrompt = () => {
    setPromptDismissed(true);
    try { window.localStorage.setItem(POOL_PROMPT_DISMISSED, '1'); } catch { /* ignore */ }
  };

  const resetAddForm = () => {
    setNewPerson(null); setNewRole(''); setNewHours(String(DEFAULT_HOURS_PER_DAY)); setNewRate(''); setAdding(false);
  };

  const addMember = async () => {
    if (!pool || !newPerson || saving) return;
    if (members.some(m => m.dagrun_pm_userid === newPerson.aadObjectId)) {
      setError(`${newPerson.displayName} is already in the pool.`);
      return;
    }
    setSaving(true); setError(null);
    try {
      const hours = Math.max(0, Math.min(24, Number(newHours) || DEFAULT_HOURS_PER_DAY));
      const rate = newRate.trim() === '' ? null : Math.max(0, Number(newRate) || 0);
      await createPoolMember(pca, {
        poolId: pool.dagrun_pm_resourcepoolid,
        userId: newPerson.aadObjectId,
        userName: newPerson.displayName,
        userEmail: newPerson.mail ?? newPerson.userPrincipalName ?? null,
        role: newRole.trim() || null,
        hoursPerDay: hours,
        hourlyRate: rate,
      });
      resetAddForm();
      reloadMembers(pool.dagrun_pm_resourcepoolid);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add member');
    } finally { setSaving(false); }
  };

  const commitField = async (m: ResourcePoolMember, field: string, value: string | number | null) => {
    setMembers(prev => prev.map(x =>
      x.dagrun_pm_resourcepoolmemberid === m.dagrun_pm_resourcepoolmemberid ? { ...x, [field]: value } : x));
    try {
      await updatePoolMember(pca, m.dagrun_pm_resourcepoolmemberid, { [field]: value });
    } catch {
      if (pool) reloadMembers(pool.dagrun_pm_resourcepoolid);
    }
  };

  // Fetch the live assigned-task count when a removal is requested so the
  // modal can warn about active assignments before confirming.
  useEffect(() => {
    if (!removeTarget?.dagrun_pm_userid) { setRemoveTaskCount(removeTarget ? 0 : null); return; }
    let cancelled = false;
    setRemoveTaskCount(null);
    countActiveAssignedTasks(pca, removeTarget.dagrun_pm_userid)
      .then(n => { if (!cancelled) setRemoveTaskCount(n); })
      .catch(() => { if (!cancelled) setRemoveTaskCount(0); });
    return () => { cancelled = true; };
  }, [pca, removeTarget]);

  const confirmRemove = async () => {
    if (!removeTarget || removing) return;
    setRemoving(true);
    try {
      await deletePoolMember(pca, removeTarget.dagrun_pm_resourcepoolmemberid);
      setRemoveTarget(null);
      if (pool) reloadMembers(pool.dagrun_pm_resourcepoolid);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove member');
    } finally { setRemoving(false); }
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div className="eyebrow">RESOURCE POOL</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
            The tenant-wide pool of people available to projects. Projects that opt in draw
            default rates and availability from here; per-project overrides always win.
          </div>
        </div>
        {pool && !adding && (
          <button className="btn-primary" onClick={() => setAdding(true)}>+ Add Member</button>
        )}
      </div>

      {error && <div style={{ marginBottom: 10, fontSize: 12, color: '#DC2626' }}>{error}</div>}

      {pool === undefined ? (
        <div className="spinner" style={{ margin: '40px auto' }} />
      ) : pool === null ? (
        <div>
          {userCount !== null && userCount > 10 && !promptDismissed && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '12px 14px', marginBottom: 14,
              background: 'var(--brand-mist)', borderRadius: 'var(--r-md)',
              border: '1px solid var(--brand)',
            }}>
              <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-2)' }}>
                <strong style={{ color: 'var(--brand)' }}>Set up your resource pool.</strong>{' '}
                Your tenant has {userCount} users — a shared resource pool gives every project
                consistent default rates and availability.
              </div>
              <button type="button" onClick={dismissPrompt} aria-label="Dismiss"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 16, lineHeight: 1, padding: 0 }}
              >×</button>
            </div>
          )}
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
            No resource pool yet. One pool serves the whole tenant.
            <div>
              <button className="btn-primary" style={{ marginTop: 14 }} onClick={createPool} disabled={creatingPool}>
                {creatingPool ? 'Creating…' : 'Create Resource Pool'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {adding && (
            <div style={{ padding: 12, background: 'var(--surface-2)', borderRadius: 6, marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <label style={BM_LABEL}>Person</label>
                <PeoplePicker pca={pca} value={newPerson} onChange={setNewPerson} placeholder="Search users…" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={BM_LABEL}>Role</label>
                  <input value={newRole} onChange={e => setNewRole(e.target.value)} style={BM_INPUT} placeholder="e.g. Senior Engineer" />
                </div>
                <div>
                  <label style={BM_LABEL}>Hours / day</label>
                  <input type="number" step="0.5" min={0} max={24} value={newHours} onChange={e => setNewHours(e.target.value)} style={BM_INPUT} />
                </div>
                <div>
                  <label style={BM_LABEL}>Rate / hr</label>
                  <input type="number" step="1" min={0} value={newRate} onChange={e => setNewRate(e.target.value)} style={BM_INPUT} placeholder="Optional" />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                <BMCancelButton onClick={resetAddForm} disabled={saving} />
                <BMPrimaryButton onClick={addMember} disabled={saving || !newPerson}>
                  {saving ? 'Adding…' : 'Add to Pool'}
                </BMPrimaryButton>
              </div>
            </div>
          )}

          {members.length === 0 && !adding ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
              No members yet. Add people to make them available to every opted-in project.
            </div>
          ) : members.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Name</th>
                <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Email</th>
                <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Role</th>
                <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Hours/day</th>
                <th style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Rate/hr</th>
                <th style={{ padding: 8 }} />
              </tr></thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.dagrun_pm_resourcepoolmemberid} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                    <td style={{ padding: 10, fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>{m.dagrun_pm_username ?? '—'}</td>
                    <td style={{ padding: 10, fontSize: 12, color: 'var(--ink-3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.dagrun_pm_useremail ?? '—'}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <InlineTextCell
                        value={m.dagrun_pm_role ?? ''}
                        placeholder="Role"
                        onCommit={v => commitField(m, 'dagrun_pm_role', v.trim() || null)}
                      />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <InlineNumberCell
                        value={m.dagrun_pm_defaulthoursperday ?? DEFAULT_HOURS_PER_DAY}
                        min={0} max={24} step={0.5} width={70}
                        onCommit={v => commitField(m, 'dagrun_pm_defaulthoursperday', v ?? DEFAULT_HOURS_PER_DAY)}
                      />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <InlineNumberCell
                        value={m.dagrun_pm_defaulthourlyrate ?? null}
                        min={0} step={1} width={84} placeholder="—"
                        onCommit={v => commitField(m, 'dagrun_pm_defaulthourlyrate', v)}
                      />
                    </td>
                    <td style={{ padding: 10, textAlign: 'right' }}>
                      <button type="button" onClick={() => setRemoveTarget(m)}
                        aria-label={`Remove ${m.dagrun_pm_username ?? 'member'}`}
                        style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#DC2626')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}
                      >×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {removeTarget && (
        <BoardModal open title="Remove Member" onClose={() => !removing && setRemoveTarget(null)}
          footer={<>
            <BMCancelButton onClick={() => setRemoveTarget(null)} disabled={removing} />
            <BMDangerButton onClick={confirmRemove} disabled={removing || removeTaskCount === null}>
              {removing ? 'Removing…' : 'Remove'}
            </BMDangerButton>
          </>}>
          Remove <strong style={{ color: '#0F1117' }}>{removeTarget.dagrun_pm_username}</strong> from the resource pool?
          {removeTaskCount === null ? (
            <div style={{ marginTop: 10, fontSize: 12, color: '#9198A8' }}>Checking active assignments…</div>
          ) : removeTaskCount > 0 ? (
            <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEF3C7', borderRadius: 6, fontSize: 12, color: '#92400E' }}>
              They are currently assigned to {removeTaskCount} active project task{removeTaskCount === 1 ? '' : 's'}.
              Removing them from the pool does not unassign those tasks or delete any data.
            </div>
          ) : null}
        </BoardModal>
      )}
    </div>
  );
}

// Inline-edit cells: draft locally, commit on blur (Enter blurs). The draft
// resets whenever the underlying value changes (e.g. a reload after a
// failed PATCH snaps the cell back).
function InlineTextCell({ value, placeholder, onCommit }: {
  value: string; placeholder?: string; onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <input
      value={draft}
      placeholder={placeholder}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { if (draft !== value) onCommit(draft); }}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDraft(value); }}
      style={{ ...BM_INPUT, padding: '4px 8px', fontSize: 12, width: '100%', maxWidth: 180 }}
    />
  );
}

function InlineNumberCell({ value, min, max, step, width, placeholder, onCommit }: {
  value: number | null; min?: number; max?: number; step?: number; width: number;
  placeholder?: string; onCommit: (v: number | null) => void;
}) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);
  const commit = () => {
    if (draft.trim() === '') { if (value !== null) onCommit(null); return; }
    let n = Number(draft);
    if (Number.isNaN(n)) { setDraft(value == null ? '' : String(value)); return; }
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    if (n !== value) onCommit(n);
  };
  return (
    <input
      type="number"
      value={draft}
      min={min} max={max} step={step}
      placeholder={placeholder}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDraft(value == null ? '' : String(value)); }}
      style={{ ...BM_INPUT, padding: '4px 8px', fontSize: 12, width }}
    />
  );
}
